import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/query'

import { RootState } from './store'
import getBaseUrl from 'utils/helpers/apiHelper'
import { Mutex } from 'async-mutex'
import { tokenReceived } from 'app/slices/actions'
import axiosApi from './axiosApi'
import { FixMeLater } from 'types'
import { captureHandledError } from 'utils/sentry'

const baseUrl = getBaseUrl() + 'api/'

const TIMEOUT = 30 * 1000

const mutex = new Mutex()
const w = globalThis as FixMeLater

type RefreshTokenResponse = {
  accessToken: string
  refreshToken?: string
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include',
  timeout: TIMEOUT,
  prepareHeaders: (headers, { getState }) => {
    const { accessToken } = (getState() as RootState).auth
    const lang =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language
        : 'tr-TR'
    headers.set('Accept-Language', lang)
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }
    return headers
  },
})

const getRequestInfo = (args: string | FetchArgs) => {
  if (typeof args === 'string') {
    return {
      url: args,
      method: 'GET',
    }
  }

  return {
    url: args?.url,
    method: args?.method ?? 'GET',
  }
}

const shouldCaptureApiError = (error: FetchBaseQueryError) => {
  if (typeof error.status === 'number') {
    return error.status >= 500
  }

  return ['FETCH_ERROR', 'PARSING_ERROR', 'TIMEOUT_ERROR', 'CUSTOM_ERROR'].includes(error.status)
}

const captureApiError = (error: FetchBaseQueryError, args: string | FetchArgs, source: string) => {
  if (!shouldCaptureApiError(error)) return

  captureHandledError(error, {
    tags: {
      source,
      status: String(error.status),
    },
    extra: {
      request: getRequestInfo(args),
      error,
    },
  })
}

const getNumericStatus = (error?: FetchBaseQueryError) => {
  return error && typeof error.status === 'number' ? error.status : 0
}

const refreshAccessTokenAndRetry = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: NonNullable<unknown>,
  currentResult: FixMeLater,
) => {
  if (mutex.isLocked()) {
    await mutex.waitForUnlock()
    return rawBaseQuery(args, api, extraOptions)
  }

  const release = await mutex.acquire()

  try {
    const { refreshToken } = (api.getState() as RootState).auth
    if (!refreshToken) {
      api.dispatch({ type: 'RESET' })
      return currentResult
    }

    const refreshResult = await rawBaseQuery(
      {
        url: 'public/refreshToken', 
        method: 'POST',
        body: { refreshToken },
      },
      api,
      extraOptions,
    )

    if (!refreshResult.data) {
      if (refreshResult.error) {
        captureApiError(refreshResult.error, 'public/refreshToken', 'rtk_query.refresh_token')
      }

      api.dispatch({ type: 'RESET' })
      return currentResult
    }

    const data = refreshResult.data as RefreshTokenResponse
    // state + axios header
    api.dispatch(tokenReceived(data))
    axiosApi.setHeader(data.accessToken)
    if (w.api) {
      w.api.send('accessToken', { accessToken: data.accessToken })
    }

    // original request retry
    return rawBaseQuery(args, api, extraOptions)
  } finally {
    release()
  }
}

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // invalid url guard
  const url = typeof args === 'string' ? args : args?.url ?? ''
  if (url.includes('undefined')) {
    const error: FetchBaseQueryError = { status: 'CUSTOM_ERROR', error: 'Invalid URL' }
    captureApiError(error, args, 'rtk_query.invalid_url')
    return { error }
  }

  await mutex.waitForUnlock()
  let result = await rawBaseQuery(args, api, extraOptions)

  const status = getNumericStatus(result.error)
  if (status !== 401) {
    if (result.error) {
      captureApiError(result.error, args, 'rtk_query')
    }

    return result
  }

  // 401 -> refresh
  result = await refreshAccessTokenAndRetry(args, api, extraOptions, result)

  if (result.error) {
    captureApiError(result.error, args, 'rtk_query.after_reauth')
  }

  return result
}

export const api = createApi({
  reducerPath: 'apis',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'INITIALIZE_SYSTEM',
    'CONFIG',
    'AUTH',
    'LOGOUT',
    'REGISTER',
    'GET_USER',
    'GET_USER_FOR_ADMIN',
    'DELETE_USER',
    'UPDATE_USER',
    'UPDATE_USER_ARCHIVE',
    'SEND_MESSAGE',
    'GET_MESSAGE',
    'SEND_THREAD_MESSAGE',
    'SEND_PINNED_MESSAGE',
    'GET_PINNED_MESSAGES',
    'GET_MESSAGES_BY_PAGE',
    'GET_ROOM',
    'GET_USERS_BY_EMAIL',
    'GET_THREAD_MESSAGE',
    'UPDATE_NOTIFICATION',
    'CREATE_CONVERSATION',
    'SEARCH_CONVERSATION',
    'GET_GROUP',
    'GET_GROUPS_FOR_ADMIN',
    'GET_CHANNELS_FOR_ADMIN',
    'CREATE_CHANNEL',
    'CREATE_GROUP',
    'GET_MEMBERS',
    'UPDATE_GROUP',
    'UPDATE_REACTION',
    'DELETE_MESSAGE',
    'EDIT_MESSAGE',
    'GET_USERS_WITHOUT_GROUP',
    'UPDATE_CHANNEL',
    'GET_CHANNELS',
    'GET_ROOM_FILES',
    'GET_ROOM_SEARCH_MESSAGES',
    'GET_ROOM_SEARCH_FILES',
    'GET_JUMP_MESSAGES',
    'READ_MESSAGES',
    'GET_USERS_FOR_MENTION',
    'SEND_VANISH_MESSAGE',
    'LDAP_SYNC_LOGS',
    'GET_LDAP_SERVER_LIST',
    'GET_LDAP_SERVER',
    'GET_LDAP_SYNC_LIST',
    'CREATE_NEW_SERVER',
    'CHECK_LDAP_CONNECTION',
    'CREATE_LDAP_SERVER',
    'CREATE_LDAP_SERVER_WITH_CONFERENCE',
    'CREATE_LDAP_GROUP_WITH_CONFERENCE',
    'SYNCHRONIZE_LDAP_TO_MONGO',
    'PAIR_LDAP_SERVER',
    'UPDATE_LDAP_SERVER',
    'CREATE_NEW_SETTING',
    'UPDATE_SYSTEM_SETINGS',
    'UPDATE_SYSTEM_PASSWORD_SETTINGS',
    'CREATE_PASSWORD_SETTINGS',
    'UPDATE_PASSWORD',
    'CREATE_MAIL_SETTINGS',
    'UPDATE_MAIL_SETTINGS',
    'SEND_OTR_RESPONSE',
    'GET_USERS_BY_ROOM_ID',
    'UPDATE_APPARANCE',
    'UPDATE_ARCHIVE',
    'LEAVE_CHANNEL',
    'REMOVE_CHANNEL_MEMBER',
    'ADD_CHANNEL_MEMBER',
    'INVITE_CHANNEL',
    'UPDATE_USER_BY_ID',
    'UPDATE_USER_PERMISSION',
    "UPDATE_USER_FCM_TOKEN",
    'SEND_POST',
    'SEND_COMMENT',
    'LIKE_POST',
    'DISLIKE_POST',
    'DELETE_POST',
    'EDIT_POST',
    'ADD_GROUP_MEMBER',
    'REMOVE_GROUP_MEMBER',
    'REINVITE_CHANNEL',
    'VIEW_MESSAGES',
    'DELETE_OTR',
    'SEARCH_QUERY',
    'LEAVE_OTR',
    'GET_POSTS',
    'GET_UNREAD_POSTS',
    'INITIAL_GET_POSTS',
    'GET_UNREAD_NOTIFICATION',
    'UNVIEW_MESSAGES',
    'REGISTER_CLIENT',
    'RE_CONNECTION',
    'GET_ALL_CHANNELS',
    'GET_UNREAD_COMMENTS_NOTIFICATION',
    'GET_NEWS_FEED_NOTIFICATION',
    'GET_NEWS_FEED_NOTIFICATION_COUNT',
    'GET_NEWS_FEED_JUMPED_COMMENTS',
    'GET_THREAD_NOTIFICATIONS',
    'GET_ROLES',
    'GET_TOKEN',
    'CHECK_COOKIE_SAFE',
  ],
  endpoints: () => ({}),
})

export default api
